import React, { useCallback, useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { DND_DELAY_MOBILE } from '../../utils/dndConstants';
import {
  getSubtaskStats,
  findPreviousSibling,
  matchesTimeFilter,
  taskOrSubtasksMatchTimeFilter,
  getAllSubtaskDueDates,
  hasIncompleteStatus,
  isTaskBlocked,
  shouldShowInPrioritize,
  calculateOrderForParent,
  getEffectivePrioritizeSection
} from '../../utils/taskUtils';
import {
  isSequentialTask,
  getSequenceNumber,
  canCompleteTask
} from '../../utils/sequentialTasks';
import ExpandButton from './ExpandButton/ExpandButton';
import ProgressIndicator from './ProgressIndicator/ProgressIndicator';
import TaskCheckbox from './TaskCheckbox/TaskCheckbox';
import TaskText from './TaskText/TaskText';
import TaskDueDate from './TaskDueDate/TaskDueDate';
import DragHandle from './DragHandle/DragHandle';
import CollapsedTaskRow from './CollapsedTaskRow/CollapsedTaskRow';
import TaskEditToolbar from './TaskEditToolbar/TaskEditToolbar';
import { SHOW_TASK_EDIT_TOOLBAR, TASK_EDIT_TOOLBAR_INLINE, TASK_EDIT_TOOLBAR_INLINE_DESKTOP_ONLY, SHOW_ADD_TASK_BUTTON } from '../../../../shared/constants/featureFlags';
import './TaskRow.css';

/** Set true to keep toolbar visible on all rows for debugging; set false before commit */
const DEBUG_INLINE_TOOLBAR_ALWAYS_VISIBLE = false;
const DEBUG_BLOCK_TOOLBAR_ALWAYS_VISIBLE = false;

function TaskRow({
  task,
  tasks,
  expandedTasks,
  editingTaskId,
  editingDueDateTaskId,
  blockingTasksHighlight,
  filter,
  topToolbarFlag = false,
  timeFilter,
  editingElementRef,
  editHighlightAnchorRef,
  onEditHighlightAnchorReady,
  dateInputCallbackRef,
  onToggleExpand,
  onCheckboxChange,
  onSetEditingTaskId,
  onSetEditingDueDateTaskId,
  onUpdateTaskDueDate,
  onTaskTextClick,
  onTaskTextFocus,
  onTaskTextInput,
  onTaskContentClick,
  onTaskTextBlur,
  onTaskTextKeyDown,
  onTaskTextPaste,
  onBlockedIconMouseEnter,
  onBlockedIconMouseLeave,
  renderTask: renderTaskProp,
  dragOverTaskId,
  dropPosition,
  currentFileId,
  onUpdateTaskOrders,
  recentlyCompletedPrioritizeIds = null,
  recentlyMovedPrioritizeIds = null,
  visiblePrioritizeSections = null,
  onFocusTaskAfterStatusSelect,
  onIndent,
  onUnindent,
  createTask,
  onMakeSequential,
  onRemoveSequential,
  onAddSubtask,
  onRequestDeleteTask,
  pendingDeleteTaskId,
  skipPlaceholderCaretToStartRef
}) {
  // Local state for hover - keep state close to usage
  const [isHovered, setIsHovered] = useState(false);
  // On mobile (no hover), always show drag handle
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 768px)').matches : false
  );
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)');
    const handler = () => setIsMobileViewport(mql.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  // Mobile inline toolbar: collapse to three dots + calendar when task text overflows. Threshold = content width minus reserved (desktop 184px; mobile 144px to match narrower expanded toolbar with indent/subtask submenu).
  const taskContentRef = useRef(null);
  const inlinePreviousOverflowRef = useRef(null);
  const [inlineToolbarTextOverflow, setInlineToolbarTextOverflow] = useState(false);
  const [inlineToolbarExpandedAfterOverflow, setInlineToolbarExpandedAfterOverflow] = useState(false);
  const [inlineCollapsedInstant, setInlineCollapsedInstant] = useState(true);

  const hasSubtasks = task.subtasks && task.subtasks.length > 0;
  const isExpanded = expandedTasks.has(task._reactKey);
  const lastSubtask = hasSubtasks ? task.subtasks[task.subtasks.length - 1] : null;
  const isEditingLastSubtask = lastSubtask && editingTaskId === lastSubtask._reactKey;
  const isEditing = editingTaskId === task._reactKey;
  const isSubtask = !!task.parent_task_id;
  const fileTasks = currentFileId != null ? tasks.filter((t) => String(t.file_id) === String(currentFileId)) : tasks;
  const canBecomeSubtask = !!findPreviousSibling(task.id, fileTasks);

  const inlineToolbarActiveForViewport =
    filter !== 'prioritize' &&
    SHOW_TASK_EDIT_TOOLBAR &&
    TASK_EDIT_TOOLBAR_INLINE &&
    (!TASK_EDIT_TOOLBAR_INLINE_DESKTOP_ONLY || !isMobileViewport);

  const INLINE_TOOLBAR_RESERVED_PX = isMobileViewport ? 154 : 184;
  /* useLayoutEffect so we measure and set overflow before first paint; avoids brief flash of full toolbar when focusing a long task */
  useLayoutEffect(() => {
    if (!isEditing || !inlineToolbarActiveForViewport || !isMobileViewport) {
      setInlineToolbarTextOverflow(false);
      if (!isEditing) {
        setInlineToolbarExpandedAfterOverflow(false);
        setInlineCollapsedInstant(true);
      }
      inlinePreviousOverflowRef.current = null;
      return;
    }
    const contentEl = taskContentRef.current;
    const textEl = editingElementRef?.current;
    if (!contentEl || !textEl) return;

    const checkOverflow = () => {
      const contentWidth = contentEl.offsetWidth;
      const textWidth = textEl.scrollWidth;
      const overflow = textWidth > contentWidth - INLINE_TOOLBAR_RESERVED_PX;
      const wasOverflow = inlinePreviousOverflowRef.current;
      setInlineToolbarTextOverflow(overflow);
      /* When collapsing: animate only if we transitioned from expanded (user was typing). If we start already over threshold (user selected a long task), show three dots immediately. */
      if (overflow) {
        setInlineCollapsedInstant(wasOverflow !== false);
        /* User typed past trigger width: collapse toolbar back to three dots even if they had expanded it. */
        setInlineToolbarExpandedAfterOverflow(false);
      } else {
        setInlineCollapsedInstant(false);
        /* Do not clear inlineToolbarExpandedAfterOverflow when overflow becomes false:
           after expand we apply max-width so text shrinks and overflow goes false;
           keep expanded state until edit ends. */
      }
      inlinePreviousOverflowRef.current = overflow;
    };

    checkOverflow();
    const ro = new ResizeObserver(checkOverflow);
    ro.observe(contentEl);
    ro.observe(textEl);
    return () => ro.disconnect();
  }, [isEditing, inlineToolbarActiveForViewport, isMobileViewport, editingElementRef]);

  let isComplete = false;
  let subtaskStats = null;
  if (hasSubtasks) {
    subtaskStats = getSubtaskStats(task);
    isComplete = subtaskStats.total > 0 && subtaskStats.completed === subtaskStats.total;
  } else {
    isComplete = task.status === 'complete' || task.status === 'wont_do';
  }
  
  const isSequential = isSequentialTask(task);
  const canComplete = canCompleteTask(task.id, tasks); // Still use task.id for server-side logic
  const isSequentialDisabled = !canComplete && !isComplete;
  
  const isBlockingTaskHighlighted = blockingTasksHighlight ? blockingTasksHighlight.has(task._reactKey) : false;
  const isBlockingTaskNotBlocked = isBlockingTaskHighlighted && canComplete && !isComplete;
  
  // Make task draggable - must be called before any early returns
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: task._reactKey,
  });

  // Make task droppable
  const { setNodeRef: setDroppableNodeRef } = useDroppable({
    id: task._reactKey,
  });

  // Mobile pre-drag: show dragging-style feedback after DND_DELAY_MOBILE ms long-press (before drag actually starts)
  const [isMobilePreDrag, setIsMobilePreDrag] = useState(false);
  const preDragTimerRef = useRef(null);
  const clearPreDragTimer = useCallback(() => {
    if (preDragTimerRef.current != null) {
      clearTimeout(preDragTimerRef.current);
      preDragTimerRef.current = null;
    }
    setIsMobilePreDrag(false);
  }, []);
  useEffect(() => {
    if (isDragging) clearPreDragTimer();
  }, [isDragging, clearPreDragTimer]);
  useEffect(() => () => clearPreDragTimer(), [clearPreDragTimer]);

  // Desktop: show same pre-drag style when drag handle is pressed (before pointer move starts the drag)
  const [isDragHandlePressed, setIsDragHandlePressed] = useState(false);
  useEffect(() => {
    if (isDragging) setIsDragHandlePressed(false);
  }, [isDragging]);
  useEffect(() => {
    if (!isDragHandlePressed) return;
    const clearPressed = () => setIsDragHandlePressed(false);
    document.addEventListener('mouseup', clearPressed);
    document.addEventListener('pointerup', clearPressed);
    return () => {
      document.removeEventListener('mouseup', clearPressed);
      document.removeEventListener('pointerup', clearPressed);
    };
  }, [isDragHandlePressed]);

  // Combine refs
  const combinedRef = (node) => {
    setNodeRef(node);
    setDroppableNodeRef(node);
  };

  // Stable ref callback so React doesn't re-run it after parent state update (avoids update loop)
  const editHighlightAnchorRefCallback = useCallback(
    (el) => {
      if (!editHighlightAnchorRef) return;
      if (el) {
        if (editHighlightAnchorRef.current !== el) {
          editHighlightAnchorRef.current = el;
          onEditHighlightAnchorReady?.();
        }
      }
      // Don't clear ref on null here; parent clears when editing ends
    },
    [editHighlightAnchorRef, onEditHighlightAnchorReady]
  );

  // All useCallback hooks must be called before any early returns
  const handleTaskItemMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleTaskItemMouseLeave = useCallback((e) => {
    const relatedTarget = e.relatedTarget;
    // Only clear hover if mouse is leaving the task item entirely
    // (not just moving to a child element like task-content or subtasks)
    if (!relatedTarget || !(relatedTarget instanceof Node) || !e.currentTarget.contains(relatedTarget)) {
      // But don't clear if moving to a subtask - subtasks should have their own hover state
      if (hasSubtasks && isExpanded && relatedTarget && relatedTarget instanceof Node) {
        const subtaskList = e.currentTarget.querySelector('.subtask-list');
        if (subtaskList && subtaskList.contains(relatedTarget)) {
          // Moving to subtask, clear parent hover (subtask will set its own hover)
          setIsHovered(false);
          return;
        }
      }
      setIsHovered(false);
    }
  }, [hasSubtasks, isExpanded]);

  const handleTaskContentMouseEnter = useCallback(() => {
    // Ensure hover state is set when hovering over task-content
    setIsHovered(true);
  }, []);

  const handleTaskContentMouseLeave = useCallback((e) => {
    // Only clear hover if not moving to a subtask
    const relatedTarget = e.relatedTarget;
    if (relatedTarget && relatedTarget instanceof Node) {
      // Check if moving to a subtask
      if (hasSubtasks && isExpanded) {
        const parentLi = e.currentTarget.closest('.task-item');
        if (parentLi) {
          const subtaskList = parentLi.querySelector('.subtask-list');
          if (subtaskList && subtaskList.contains(relatedTarget)) {
            // Moving to subtask, clear hover
            setIsHovered(false);
            return;
          }
        }
      }
      // Check if moving to another element within the same task-item
      const parentLi = e.currentTarget.closest('.task-item');
      if (parentLi && parentLi.contains(relatedTarget)) {
        // Still within task-item, don't clear hover
        return;
      }
    }
    // Leaving task-content and not moving to subtask or other task-item element
    setIsHovered(false);
  }, [hasSubtasks, isExpanded]);

  const handleFocusTaskAfterDueDateSet = useCallback(() => {
    if (editingTaskId === task._reactKey && editingElementRef?.current) {
      if (skipPlaceholderCaretToStartRef && (task._reactKey === 'placeholder' || task._reactKey === 'placeholder_client_key')) {
        skipPlaceholderCaretToStartRef.current = true;
      }
      editingElementRef.current.focus();
    }
  }, [editingTaskId, task._reactKey, editingElementRef, skipPlaceholderCaretToStartRef]);

  const handleTaskContentClick = useCallback((e) => {
    if (!isEditing) {
      // Stop if clicking on interactive elements
      if (e.target.closest('.expand-button, .task-checkbox, .task-due-date, .task-edit-toolbar')) {
        return;
      }
      onTaskContentClick(task._reactKey, e);
    }
  }, [isEditing, task._reactKey, onTaskContentClick]);

  const handleExpandButtonClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  const handleToggleExpand = useCallback(() => {
    onToggleExpand(task._reactKey);
  }, [task._reactKey, onToggleExpand]);

  const handleUnindentClick = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    onUnindent?.(task);
  }, [task, onUnindent]);

  const handleIndentClick = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    onIndent?.(task);
  }, [task, onIndent]);
  
  const matchesTimeFilterForRender = timeFilter ? matchesTimeFilter(task, timeFilter) : true;
  const hasMatchingSubtasksInTimeFilter = timeFilter && hasSubtasks && task.subtasks && task.subtasks.some(subtask => taskOrSubtasksMatchTimeFilter(subtask, timeFilter));
  
  // Determine if task should be rendered as collapsed or hidden
  const shouldRenderCollapsed = () => {
    // For incomplete filter, only show tasks that have status 'incomplete' (not wip, waiting, complete, or blocked)
    if (filter === 'incomplete') {
      const taskIsBlocked = isTaskBlocked(task, tasks);
      if (!hasIncompleteStatus(task) || taskIsBlocked) {
        return true;
      }
    }
    
    // For prioritize filter, return null instead of collapsed line to avoid showing wavy lines.
    // Keep task visible if it was just completed (recentlyCompletedPrioritizeIds) until animation + delay.
    // Also show completed tasks when the Done section is visible (they appear in that section).
    if (filter === 'prioritize' && !shouldShowInPrioritize(task, tasks)) {
      if (recentlyCompletedPrioritizeIds && recentlyCompletedPrioritizeIds.has(task.id)) {
        return false; // still render the row
      }
      if (visiblePrioritizeSections?.has('done') && task.status === 'complete') {
        return false; // show in Done section
      }
      return null;
    }
    
    // For time filter, show collapsed if task doesn't match and has no matching subtasks
    if (timeFilter && !matchesTimeFilterForRender && !hasMatchingSubtasksInTimeFilter) {
      return true;
    }
    
    return false;
  };
  
  const renderState = shouldRenderCollapsed();
  if (renderState === null) {
    return null;
  }
  if (renderState === true) {
    return <CollapsedTaskRow combinedRef={combinedRef} attributes={attributes} listeners={listeners} />;
  }

  const style = {
    ...(transform ? {
      transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    } : {}),
    ...(isDragging ? {
      position: 'relative',
      zIndex: 1000,
    } : {}),
  };

  const isDragOver = dragOverTaskId === task._reactKey;
  const dropClass = isDragOver && dropPosition ? `task-item-drop-${dropPosition}` : '';

  // Helper function to build className
  const getTaskItemClassName = () => {
    const classes = ['task-item'];
    if (isComplete) classes.push('task-complete');
    if (isEditing) classes.push('task-item-editing');
    if (isSequentialDisabled) classes.push('task-sequential-disabled');
    if (isBlockingTaskHighlighted) classes.push('task-blocking-highlighted');
    if (isBlockingTaskNotBlocked) classes.push('task-blocking-not-blocked');
    if (isDragging) classes.push('task-item-dragging');
    if (isMobilePreDrag || isDragHandlePressed) classes.push('task-item-mobile-pre-drag');
    if (dropClass) classes.push(dropClass);
    if (filter === 'prioritize') classes.push('task-item-prioritize-view');
    if (filter === 'prioritize' && recentlyMovedPrioritizeIds?.has(task.id)) classes.push('task-item-prioritize-moved-highlight');
    if (pendingDeleteTaskId === task.id) classes.push('task-item-pending-delete');
    return classes.join(' ');
  };

  // Mobile: wrap dnd-kit touch listeners to add pre-drag feedback after DND_DELAY_MOBILE ms.
  // Do not start drag when touch begins on the checkbox (long-press there opens the status dropdown only).
  const mobileTouchProps = isMobileViewport
    ? {
        ...attributes,
        ...listeners,
        onTouchStart: (e) => {
          if (e.target.closest?.('[data-task-checkbox]')) {
            clearPreDragTimer();
            return;
          }
          clearPreDragTimer();
          preDragTimerRef.current = setTimeout(() => setIsMobilePreDrag(true), DND_DELAY_MOBILE);
          listeners?.onTouchStart?.(e);
        },
        onTouchMove: (e) => {
          clearPreDragTimer();
          listeners?.onTouchMove?.(e);
        },
        onTouchEnd: (e) => {
          clearPreDragTimer();
          listeners?.onTouchEnd?.(e);
        },
        onTouchCancel: (e) => {
          clearPreDragTimer();
          listeners?.onTouchCancel?.(e);
        }
      }
    : null;

  // Desktop: wrap drag-handle listeners to show pre-drag style on press
  const desktopDragHandleListeners =
    !isMobileViewport && listeners
      ? {
          ...listeners,
          onMouseDown: (e) => {
            setIsDragHandlePressed(true);
            listeners.onMouseDown?.(e);
          },
          onPointerDown: (e) => {
            setIsDragHandlePressed(true);
            listeners.onPointerDown?.(e);
          }
        }
      : listeners;

  return (
    <li 
      ref={combinedRef}
      style={style}
      className={getTaskItemClassName()}
      data-task-id={task.id}
      data-react-key={task._reactKey}
      onMouseEnter={handleTaskItemMouseEnter}
      onMouseLeave={handleTaskItemMouseLeave}
    >
      <div className="task-content-wrapper">
        <div
          ref={(el) => {
            taskContentRef.current = el;
            if (editHighlightAnchorRef && editHighlightAnchorRefCallback) {
              editHighlightAnchorRefCallback(isEditing ? el : null);
            }
          }}
          className={`task-content${isMobileViewport ? ' task-content-draggable-by-long-press' : ''}${!topToolbarFlag && inlineToolbarActiveForViewport ? ' task-content-inline-toolbar-visible' : ''}${inlineToolbarExpandedAfterOverflow ? ' task-content-inline-toolbar-expanded' : ''}${!topToolbarFlag && ((inlineToolbarActiveForViewport && (isEditing || DEBUG_INLINE_TOOLBAR_ALWAYS_VISIBLE)) || (!inlineToolbarActiveForViewport && (isEditing || DEBUG_BLOCK_TOOLBAR_ALWAYS_VISIBLE))) && task.due_date ? ' task-content-due-in-toolbar' : ''}`}
          onMouseEnter={handleTaskContentMouseEnter}
          onMouseLeave={handleTaskContentMouseLeave}
          onClick={handleTaskContentClick}
          {...(mobileTouchProps ?? (isMobileViewport ? { ...listeners, ...attributes } : {}))}
        >
        {!isMobileViewport && (isHovered || filter === 'prioritize') && (
          <DragHandle
            hasSubtasks={hasSubtasks}
            listeners={desktopDragHandleListeners}
            attributes={attributes}
          />
        )}
        <span onClick={handleExpandButtonClick} className="expand-button-desktop" style={{ display: 'inline-block' }}>
          <ExpandButton
            hasSubtasks={hasSubtasks}
            isExpanded={isExpanded}
            onToggleExpand={handleToggleExpand}
          />
        </span>
        {!SHOW_TASK_EDIT_TOOLBAR && isMobileViewport && isEditing && (
          <>
            <button
              type="button"
              className="task-unindent-button"
              onClick={handleUnindentClick}
              onMouseDown={(e) => e.preventDefault()}
              disabled={!isSubtask || !onUnindent}
              aria-label="Outdent task"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 16 16" aria-hidden>
                <g fill="currentColor" fillRule="evenodd">
                  <path d="M13 8a.5.5 0 0 0-.5-.5H5.707l2.147-2.146a.5.5 0 1 0-.708-.708l-3 3a.5.5 0 0 0 0 .708l3 3a.5.5 0 0 0 .708-.708L5.707 8.5H12.5A.5.5 0 0 0 13 8"/>
                  <path d="M3.5 4a.5.5 0 0 0-.5.5v7a.5.5 0 0 0 1 0v-7a.5.5 0 0 0-.5-.5"/>
                </g>
              </svg>
            </button>
            <button
              type="button"
              className="task-indent-button"
              onClick={handleIndentClick}
              onMouseDown={(e) => e.preventDefault()}
              disabled={!canBecomeSubtask || !onIndent}
              aria-label="Indent as subtask"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 16 16" aria-hidden>
                <g fill="currentColor" fillRule="evenodd">
                  <path d="M3 8a.5.5 0 0 1 .5-.5h6.793L8.146 5.354a.5.5 0 1 1 .708-.708l3 3a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708-.708L10.293 8.5H3.5A.5.5 0 0 1 3 8"/>
                  <path d="M12.5 4a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 .5-.5"/>
                </g>
              </svg>
            </button>
          </>
        )}
        {hasSubtasks && !isSequentialDisabled ? (
          <ProgressIndicator
            task={task}
            tasks={tasks}
            subtaskStats={subtaskStats}
            isSequentialDisabled={isSequentialDisabled}
            isBlockingTaskHighlighted={isBlockingTaskHighlighted}
            isBlockingTaskNotBlocked={isBlockingTaskNotBlocked}
            onBlockedIconMouseEnter={onBlockedIconMouseEnter}
            onBlockedIconMouseLeave={onBlockedIconMouseLeave}
          />
        ) : (
          <TaskCheckbox
            task={task}
            isSequential={isSequential}
            isSequentialDisabled={isSequentialDisabled}
            onCheckboxChange={onCheckboxChange}
            onBlockedIconMouseEnter={onBlockedIconMouseEnter}
            onBlockedIconMouseLeave={onBlockedIconMouseLeave}
            onFocusTaskAfterStatusSelect={onFocusTaskAfterStatusSelect}
            disableLongPress={
              SHOW_TASK_EDIT_TOOLBAR &&
              !(
                TASK_EDIT_TOOLBAR_INLINE &&
                TASK_EDIT_TOOLBAR_INLINE_DESKTOP_ONLY &&
                !isMobileViewport
              )
            }
          />
        )}
        <TaskText
          task={task}
          tasks={tasks}
          isEditing={isEditing}
          isComplete={isComplete}
          hasSubtasks={hasSubtasks}
          filter={filter}
          isMobile={isMobileViewport}
          editingElementRef={editingElementRef}
          onTaskTextClick={onTaskTextClick}
          onTaskTextFocus={onTaskTextFocus}
          onTaskTextInput={onTaskTextInput}
          onTaskTextBlur={onTaskTextBlur}
          onTaskTextKeyDown={onTaskTextKeyDown}
          onTaskTextPaste={onTaskTextPaste}
        />
        {!topToolbarFlag && inlineToolbarActiveForViewport && (isEditing || DEBUG_INLINE_TOOLBAR_ALWAYS_VISIBLE) && !isDragging && (
          <TaskEditToolbar
            task={task}
            tasks={tasks}
            currentStatus={task.status}
            onStatusSelect={onCheckboxChange}
            hasSubtasks={hasSubtasks}
            subtaskStats={subtaskStats}
            isSequentialDisabled={isSequentialDisabled}
            canUnindent={isSubtask && !!onUnindent}
            canIndent={canBecomeSubtask && !!onIndent}
            onUnindent={onUnindent}
            onIndent={onIndent}
            onUpdateTaskDueDate={onUpdateTaskDueDate}
            onFocusAfterDueDateSet={handleFocusTaskAfterDueDateSet}
            onMakeSequential={onMakeSequential}
            onRemoveSequential={onRemoveSequential}
            onAddSubtask={onAddSubtask}
            onRequestDeleteTask={onRequestDeleteTask}
            filter={filter}
            inline
            isMobile={isMobileViewport}
            inlineCollapsed={inlineToolbarTextOverflow && !inlineToolbarExpandedAfterOverflow}
            inlineCollapsedInstant={inlineCollapsedInstant && (inlineToolbarTextOverflow && !inlineToolbarExpandedAfterOverflow)}
            inlineExpandedByUser={inlineToolbarExpandedAfterOverflow}
            onInlineExpandClick={() => setInlineToolbarExpandedAfterOverflow(true)}
          />
        )}
        {(!isEditing || isMobileViewport || topToolbarFlag || (filter === 'prioritize' && !!visiblePrioritizeSections && ['no-due-date', 'done'].includes(getEffectivePrioritizeSection(task, visiblePrioritizeSections)))) && (
          <TaskDueDate
            task={task}
            hasSubtasks={hasSubtasks}
            isComplete={isComplete}
            editingDueDateTaskId={editingDueDateTaskId}
            isHovered={isHovered}
            dateInputCallbackRef={dateInputCallbackRef}
            onSetEditingDueDateTaskId={onSetEditingDueDateTaskId}
            onUpdateTaskDueDate={onUpdateTaskDueDate}
            hideEmptyDueDatePlaceholder={
              SHOW_TASK_EDIT_TOOLBAR &&
              !(filter === 'prioritize' && visiblePrioritizeSections && ['no-due-date', 'done'].includes(getEffectivePrioritizeSection(task, visiblePrioritizeSections)))
            }
            showIconOnlyWhenEmpty={
              filter === 'prioritize' &&
              !!visiblePrioritizeSections &&
              ['no-due-date', 'done'].includes(getEffectivePrioritizeSection(task, visiblePrioritizeSections))
            }
          />
        )}
        <span
          onClick={handleExpandButtonClick}
          className={`expand-button-mobile${isMobileViewport && isEditing && hasSubtasks ? ' expand-button-mobile-hidden-when-editing-parent' : ''}`}
        >
          <ExpandButton
            hasSubtasks={hasSubtasks}
            isExpanded={isExpanded}
            onToggleExpand={handleToggleExpand}
          />
        </span>
        </div>
        {!topToolbarFlag && filter !== 'prioritize' && SHOW_TASK_EDIT_TOOLBAR && !inlineToolbarActiveForViewport && (isEditing || DEBUG_BLOCK_TOOLBAR_ALWAYS_VISIBLE) && !isDragging && (
          <TaskEditToolbar
            task={task}
            tasks={tasks}
            currentStatus={task.status}
            onStatusSelect={onCheckboxChange}
            hasSubtasks={hasSubtasks}
            subtaskStats={subtaskStats}
            isSequentialDisabled={isSequentialDisabled}
            canUnindent={isSubtask && !!onUnindent}
            canIndent={canBecomeSubtask && !!onIndent}
            onUnindent={onUnindent}
            onIndent={onIndent}
            onUpdateTaskDueDate={onUpdateTaskDueDate}
            onFocusAfterDueDateSet={handleFocusTaskAfterDueDateSet}
            onMakeSequential={onMakeSequential}
            onRemoveSequential={onRemoveSequential}
            onAddSubtask={onAddSubtask}
            onRequestDeleteTask={onRequestDeleteTask}
            filter={filter}
          />
        )}
      </div>
      {hasSubtasks && isExpanded && renderTaskProp && (
        <ul className="subtask-list">
          {task.subtasks.map(subtask => renderTaskProp(subtask))}
          {SHOW_ADD_TASK_BUTTON && createTask && onSetEditingTaskId && (
            <li className={`add-task-row add-task-row-subtask${lastSubtask && lastSubtask.subtasks && lastSubtask.subtasks.length > 0 ? ' add-task-row-after-parent' : ''}${lastSubtask && isSequentialTask(lastSubtask) ? ' add-task-row--dual' : ''}`}>
              {isEditingLastSubtask ? (
                <button
                  type="button"
                  className="add-task-button add-task-button--hint"
                  disabled
                >
                  Press <span className="add-task-enter-icon" aria-hidden="true">&#x23CE;</span> to add {lastSubtask && isSequentialTask(lastSubtask) ? 'sequential ' : ''}task
                </button>
              ) : lastSubtask && isSequentialTask(lastSubtask) ? (
                <span className="add-task-row__options">
                  <button
                    type="button"
                    className="add-task-button add-task-button--inline"
                    onClick={() => {
                      const order = calculateOrderForParent(task.id, tasks);
                      const initialText = (getSequenceNumber(lastSubtask) + 1) + '.\u00A0';
                      const newTask = createTask(initialText, task.id, order);
                      if (newTask) onSetEditingTaskId(newTask._reactKey);
                    }}
                  >
                    Add sequential task
                  </button>
                  <span className="add-task-row__separator"> or </span>
                  <button
                    type="button"
                    className="add-task-button add-task-button--inline"
                    onClick={() => {
                      const order = calculateOrderForParent(task.id, tasks);
                      const newTask = createTask('', task.id, order);
                      if (newTask) onSetEditingTaskId(newTask._reactKey);
                    }}
                  >
                    regular task...
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  className="add-task-button"
                  onClick={() => {
                    const order = calculateOrderForParent(task.id, tasks);
                    const newTask = createTask('', task.id, order);
                    if (newTask) onSetEditingTaskId(newTask._reactKey);
                  }}
                >
                  Add task...
                </button>
              )}
            </li>
          )}
        </ul>
      )}
    </li>
  );
}

export default TaskRow;


